import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ProjectSummary } from '@gc/shared';
import { ApiError, apiRequest } from '../api/client.js';
import { archivarProyecto, listarProyectos, restaurarProyecto } from '../api/projects.js';

type Carga =
  | { readonly tipo: 'cargando' }
  | { readonly tipo: 'listo'; readonly proyectos: ProjectSummary[] }
  | { readonly tipo: 'error'; readonly mensaje: string };

const PRODUCTOS = [
  { tipo: 'VIDEO_PODCAST', nombre: 'Vídeo / Podcast' },
  { tipo: 'ADVANCED_VIDEO', nombre: 'Vídeo avanzado' },
  { tipo: 'VIRTUAL_CREATOR', nombre: 'Creador virtual' },
] as const;

const ESTADOS: Record<string, string> = {
  DRAFT: 'Borrador',
  GENERATING: 'Generando',
  REVIEW: 'En revisión',
  READY: 'Aprobado',
  RENDERING: 'Renderizando',
  COMPLETED: 'Completado',
  ERROR: 'Error',
};

export function Dashboard(): React.JSX.Element {
  const [carga, setCarga] = useState<Carga>({ tipo: 'cargando' });
  const [verArchivados, setVerArchivados] = useState(false);
  // Lo decide el servidor (fase 09 se habilita por configuración): la interfaz
  // no muestra una acción que aparente funcionar si la API la va a rechazar.
  const [disponibles, setDisponibles] = useState<readonly string[]>(['VIDEO_PODCAST']);
  useEffect(() => {
    void apiRequest<{ available: string[] }>('/project-types')
      .then((r) => setDisponibles(r.available))
      .catch(() => undefined);
  }, []);
  const [accionando, setAccionando] = useState<string | null>(null);

  const recargar = useCallback(async () => {
    try {
      const pagina = await listarProyectos({ includeArchived: verArchivados, limit: 50 });
      setCarga({ tipo: 'listo', proyectos: pagina.data });
    } catch (causa) {
      setCarga({
        tipo: 'error',
        mensaje: causa instanceof ApiError ? causa.message : 'No se pudieron cargar los proyectos.',
      });
    }
  }, [verArchivados]);

  useEffect(() => {
    setCarga({ tipo: 'cargando' });
    void recargar();
  }, [recargar]);

  async function alternarArchivado(proyecto: ProjectSummary): Promise<void> {
    setAccionando(proyecto.id);
    try {
      if (proyecto.archivedAt === null) await archivarProyecto(proyecto.id);
      else await restaurarProyecto(proyecto.id);
      await recargar();
    } catch (causa) {
      setCarga({
        tipo: 'error',
        mensaje: causa instanceof ApiError ? causa.message : 'La operación falló.',
      });
    } finally {
      setAccionando(null);
    }
  }

  return (
    <section>
      <h1>Panel</h1>

      <h2>Tipos de producto</h2>
      <ul style={{ listStyle: 'none', padding: 0, display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
        {PRODUCTOS.map((producto) => (
          <li
            key={producto.tipo}
            style={{
              border: '1px solid var(--borde)',
              borderRadius: '6px',
              padding: '0.75rem 1rem',
              opacity: disponibles.includes(producto.tipo) ? 1 : 0.6,
            }}
          >
            <strong>{producto.nombre}</strong>
            <br />
            {disponibles.includes(producto.tipo) ? (
              <Link to={producto.tipo === 'VIDEO_PODCAST' ? '/proyectos/nuevo' : `/proyectos/nuevo?tipo=${producto.tipo}`}>
                Crear proyecto
              </Link>
            ) : (
              // Sin acción que aparente funcionar (docs/10_UI_UX.md).
              <span>Próximamente</span>
            )}
          </li>
        ))}
      </ul>

      <h2>Mis proyectos</h2>
      <p>
        <label>
          <input
            type="checkbox"
            checked={verArchivados}
            onChange={(evento) => setVerArchivados(evento.target.checked)}
          />{' '}
          Mostrar archivados
        </label>
      </p>

      {carga.tipo === 'cargando' && <p role="status">Cargando proyectos…</p>}
      {carga.tipo === 'error' && <p role="alert">{carga.mensaje}</p>}

      {carga.tipo === 'listo' && carga.proyectos.length === 0 && (
        <p>
          Todavía no tienes proyectos. <Link to="/proyectos/nuevo">Crea el primero</Link>.
        </p>
      )}

      {carga.tipo === 'listo' && carga.proyectos.length > 0 && (
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <caption style={{ textAlign: 'left', paddingBottom: '0.5rem' }}>
            {carga.proyectos.length} proyecto(s)
          </caption>
          <thead>
            <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--borde)' }}>
              <th scope="col">Nombre</th>
              <th scope="col">Estado</th>
              <th scope="col">Escenas</th>
              <th scope="col">Actualizado</th>
              <th scope="col">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {carga.proyectos.map((proyecto) => (
              <tr key={proyecto.id} style={{ borderBottom: '1px solid var(--borde-suave)' }}>
                <td>
                  <Link to={`/proyectos/${proyecto.id}`}>{proyecto.name}</Link>
                  {proyecto.archivedAt !== null && <em> (archivado)</em>}
                </td>
                <td>{ESTADOS[proyecto.status] ?? proyecto.status}</td>
                <td>{proyecto.sceneCount}</td>
                <td>{new Date(proyecto.updatedAt).toLocaleString('es-ES')}</td>
                <td>
                  <button
                    type="button"
                    onClick={() => void alternarArchivado(proyecto)}
                    disabled={accionando === proyecto.id}
                  >
                    {proyecto.archivedAt === null ? 'Archivar' : 'Restaurar'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  );
}
